/**
 * A profile as a file a player holds.
 *
 * Browser storage is fragile: one "clear site data" and a hundred hours are gone. The download is
 * the only copy that survives that, and the only way a character reaches another machine, because
 * there is no account and no server to hold one.
 *
 * The file is an envelope around the record rather than the record itself. A bare profile on disk
 * would make "this is not one of ours" and "this is one of ours and it is broken" the same
 * failure, and those are different sentences to read out. The envelope answers the first question
 * before the profile is looked at.
 *
 * Two versions, and they cover different things. `formatVersion` is the wrapper's, and moves when
 * the wrapper changes shape. `schemaVersion` inside the profile is the save's own, and the
 * migration chain is still the only thing that moves it. A file from an older build carries an old
 * `schemaVersion` and the current wrapper, and it is `readProfile` that walks it forward.
 *
 * Nothing here is signed or obfuscated. An edited save is trivially possible, this is a PvE game
 * with no server, and the import path validates for safety rather than for trust.
 */

import { toSaveRecord } from '@/save/saveContract';
import { sanitizePlayerName } from '@/identity/playerNameSanitize';
import type { SaveRecord } from '@/save/migrationContract';
import type { SaveProfile } from '@/save/saveContract';

/** What the `format` field says, and the first thing an import checks. */
export const EXPORT_FORMAT = 'tbd.save';

/** The wrapper's version. The profile inside carries its own, and they never move together. */
export const EXPORT_FORMAT_VERSION = 1;

/** What a name falls back to in a filename when it sanitizes down to nothing printable. */
const UNNAMED_SLUG = 'character';

/** The extension. Double, so a file manager still shows it is JSON and a human can open it. */
const EXTENSION = '.tbd-save.json';

export interface ExportEnvelope {
  readonly format: typeof EXPORT_FORMAT;
  readonly formatVersion: number;
  /** Wall clock, for the human reading the file rather than for any check. */
  readonly exportedAtMs: number;
  readonly profile: SaveRecord;
}

export function toExportEnvelope(profile: SaveProfile, nowMs: number): ExportEnvelope {
  return {
    format: EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    exportedAtMs: nowMs,
    profile: toSaveRecord(profile),
  };
}

/** Pretty-printed on purpose: the file is the player's, and a save they can read is a save. */
export function toExportText(profile: SaveProfile, nowMs: number): string {
  return `${JSON.stringify(toExportEnvelope(profile, nowMs), null, 2)}\n`;
}

/**
 * `tbd-soggy-walrus-2026-08-19.tbd-save.json`.
 *
 * Who and when, because a downloads folder with six of these in it is the situation this name is
 * for. The character's level joins it when there is one to write (#24).
 *
 * The date is the local one, since it is read by the person who pressed the button, and the slug
 * goes through the same sanitizer a name off the wire does before anything else touches it: what
 * comes back here is a filename, and a filename is a place a control character must never reach.
 */
export function exportFileName(profile: SaveProfile, nowMs: number): string {
  return `tbd-${nameSlug(profile.name)}-${isoDate(nowMs)}${EXTENSION}`;
}

function nameSlug(name: string): string {
  const slug = sanitizePlayerName(name)
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/gu, '-')
    .replaceAll(/^-+|-+$/gu, '');
  return slug === '' ? UNNAMED_SLUG : slug;
}

/** Local calendar date, zero padded, which `toISOString` cannot give without shifting the day. */
function isoDate(nowMs: number): string {
  const when = new Date(nowMs);
  const month = String(when.getMonth() + 1).padStart(2, '0');
  const day = String(when.getDate()).padStart(2, '0');
  return `${String(when.getFullYear())}-${month}-${day}`;
}
